/**
 * Format a number as a currency string with the appropriate currency symbol
 * 
 * @param {number} amount - The amount to format
 * @param {string} currency - The currency code (default: 'USD')
 * @param {string} locale - The locale to use for formatting (default: 'en-US')
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (
    amount: number,
    currency: string = 'USD',
    locale: string = 'en-US'
): string => {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
    }).format(amount);
};

/**
 * Common currency codes used in the application
 */
export const CURRENCY = {
    USD: 'USD',
    LKR: 'LKR',
    EUR: 'EUR',
    GBP: 'GBP',
    MVR: 'MVR'  // Maldivian Rufiyaa
};

/**
 * Convert an amount from one currency to another using the specified exchange rate
 * 
 * @param {number} amount - The amount to convert
 * @param {number} exchangeRate - The exchange rate to apply
 * @returns {number} - The converted amount
 */
export const convertCurrency = (amount: number, exchangeRate: number): number => {
    return amount * exchangeRate;
};

/**
 * Default exchange rates relative to USD (1 USD = X units of foreign currency)
 * These should be updated from an API in a real application
 */
export const DEFAULT_EXCHANGE_RATES = {
    [CURRENCY.USD]: 1,
    [CURRENCY.LKR]: 300,
    [CURRENCY.EUR]: 0.92,
    [CURRENCY.GBP]: 0.78,
    [CURRENCY.MVR]: 15.4
};

/**
 * Format currency with the appropriate locale
 * 
 * @param {number} amount - The amount to format
 * @param {string} currency - The currency code
 * @returns {string} - Formatted currency string
 */
export const formatCurrencyByCode = (amount: number, currency: string): string => {
    const localeMap: Record<string, string> = {
        [CURRENCY.USD]: 'en-US',
        [CURRENCY.LKR]: 'si-LK',
        [CURRENCY.EUR]: 'de-DE',
        [CURRENCY.GBP]: 'en-GB',
        [CURRENCY.MVR]: 'dv-MV'
    };

    return formatCurrency(amount, currency, localeMap[currency] || 'en-US');
};
